import {
  MealWithIntakes,
  PlanCell,
  PlanCellCoordinates,
  PlanCellCoordinatesWithOptionalPerson,
  PlanCellWithIndex,
} from "../data/plan";
import {
  MealIngredient,
  MealWithIngredients,
} from "../data/mealWithIngredients";
import { isDeleted, isEdited, isNew } from "./editUtils";
import { FoodDto, PersonDto, PurchaseDto, updateMeals } from "../api";
import { mealInds } from "./mealIndUtils";
import {
  PersonRecipeUnitModification,
  RecipeUnitModification,
  StepIngredientModification,
} from "../data/dishModification";
import { FoodUnit } from "../data/foodUnit";
import { Purchase, PurchaseUnit } from "../data/purchase";
import { Intake } from "../data/intake";
import { emptyNutriValue, NutriValues } from "../data/nutriValues";
import { formatPurchaseDescription } from "./purchaseUtils";
import { formatGermanDateShort } from "./dateUtils";
import {
  captureStack,
  lookupDiagnosticsEnabled,
  reportLookupMiss,
} from "./lookupDiagnostics";

export interface FindByIdContext {
  cache?: string;
  index?: string;
  renderStack?: string;
}

/**
 * Findet eine Entity per Id in einer aus dem Query-Cache geladenen Liste.
 * Ein Miss wird – außer die Diagnose ist inaktiv – protokolliert (siehe
 * `lookupDiagnostics`), aber weiterhin als `undefined` zurückgegeben.
 */
export const findById = <T extends { id: number }>(
  items: T[] | undefined,
  id: number | undefined,
  context?: FindByIdContext,
): T | undefined => {
  if (id == undefined || items == undefined) return undefined;
  const found = items.find((item) => item.id === id);
  if (found == undefined && lookupDiagnosticsEnabled() && id > 0) {
    void reportLookupMiss({
      kind: "array-lookup",
      cache: context?.cache ?? "array",
      index: context?.index,
      requestedId: id,
      accessStack: captureStack(),
      renderStack: context?.renderStack,
      knownIndexIds: items.map((item) => item.id).slice(0, 50),
    });
  }
  return found;
};

export function formatPlanCellDescription(
  planCell: PlanCell,
  purchases: PurchaseDto[] | undefined,
) {
  return planCell.indicator == "MealWithIntakes"
    ? planCell.meal.dishDescription ||
        planCell.meal.foodDescription ||
        planCell.meal.purchaseDescription ||
        ""
    : formatPurchaseDescription(
        findById(purchases, planCell.purchaseUnitPurchaseId),
      );
}

export function isNullIndex(index: number | undefined) {
  return index == -1 || index == undefined;
}

export function isNewCell(planCell: PlanCell) {
  return planCell.indicator == "MealWithIntakes"
    ? isNullIndex(planCell.meal.id)
    : isNullIndex(planCell.id);
}

export function isNewOrEdited(planCell: PlanCell) {
  return isNewCell(planCell) || isEdited(planCell);
}

export function computePlanCellCoordinatesFromMeal(
  meal: MealWithIngredients,
  index?: number,
) {
  return {
    date: meal.mealDate,
    mealInd: meal.mealInd,
    personId: meal.personId,
    index,
  };
}

export function computePlanCellCoordinates(
  planCell: PlanCell,
  index?: number,
): PlanCellCoordinates {
  return planCell.indicator == "MealWithIntakes"
    ? computePlanCellCoordinatesFromMeal(planCell.meal, index)
    : {
        date: planCell.intakeDate,
        mealInd: planCell.mealInd,
        personId: planCell.personId,
        index,
      };
}

export function sameCoordinates(
  a: PlanCellCoordinates,
  b: PlanCellCoordinates,
) {
  return (
    a.date == b.date &&
    a.mealInd == b.mealInd &&
    a.personId == b.personId &&
    a.index == b.index
  );
}

export function convertToNewPlanCellWithIndex(
  planCell: PlanCell,
): PlanCellWithIndex {
  return { planCell, index: -1 };
}

export function computePlanCellCoordinatesFromPlanCellWithIndex(
  planCellWithIndex: PlanCellWithIndex,
) {
  return computePlanCellCoordinates(
    planCellWithIndex.planCell,
    planCellWithIndex.index,
  );
}

function nutriValuesFromFood(
  mealIngredient: MealIngredient,
  foods: FoodDto[],
  foodId: number,
) {
  const food = findById(foods, foodId);
  if (food == undefined) {
    return emptyNutriValue;
  }
  const foodUnit = food.foodUnits.find(
    (foodUnit) => foodUnit.id == mealIngredient.foodUnitId,
  )!;
  if (foodUnit == undefined) {
    return emptyNutriValue;
  }
  const foodUnitFactor =
    foodUnit.netWeight != undefined ? foodUnit.netWeight / 100 : 1;
  const nutriFactor = foodUnitFactor * mealIngredient.amount;
  return {
    fat: food.fat * nutriFactor,
    carbs: food.carbs * nutriFactor,
    protein: food.protein * nutriFactor,
    kcal: food.kcal! * nutriFactor,
  };
}

function nutriValuesFromPurchase(
  mealIngredient: MealIngredient,
  purchases: PurchaseDto[],
  purchaseId: number,
) {
  const purchase = findById(purchases, purchaseId);
  if (purchase == undefined) {
    return emptyNutriValue;
  }
  const purchaseUnit = purchase.purchaseUnits.find(
    (purchaseUnit) => purchaseUnit.id == mealIngredient.purchaseUnitId,
  )!;
  if (purchaseUnit == undefined) {
    return emptyNutriValue;
  }
  const purchaseUnitFactor =
    purchaseUnit.netWeight != undefined ? purchaseUnit.netWeight / 100 : 1;
  const nutriFactor = purchaseUnitFactor * mealIngredient.amount;
  return {
    fat: purchase.fat * nutriFactor,
    carbs: purchase.carbs * nutriFactor,
    protein: purchase.protein * nutriFactor,
    kcal: purchase.kcal! * nutriFactor,
  };
}

export function nutriValuesFromMealIngredient(
  mealIngredient: MealIngredient,
  foods: FoodDto[],
  purchases: PurchaseDto[],
) {
  return isNullIndex(mealIngredient.foodUnitFoodId)
    ? nutriValuesFromPurchase(
        mealIngredient,
        purchases,
        mealIngredient.purchaseUnitPurchaseId!,
      )
    : nutriValuesFromFood(
        mealIngredient,
        foods,
        mealIngredient.foodUnitFoodId!,
      );
}

export function nutriValuesFromIntake(
  intake: Intake,
  purchases: PurchaseDto[] | undefined,
) {
  const purchaseId = intake.purchaseUnitPurchaseId;
  const purchase = findById(purchases, purchaseId);
  if (!purchase) {
    return { fat: 0, carbs: 0, protein: 0, kcal: 0 };
  }
  const purchaseUnit = purchase.purchaseUnits.find(
    (purchaseUnit) => purchaseUnit.id == intake.purchaseUnitId,
  )!;
  if (purchaseUnit == undefined) {
    return {
      fat: 0,
      carbs: 0,
      protein: 0,
      kcal: 0,
    };
  }
  const purchaseUnitFactor =
    purchaseUnit.netWeight != undefined ? purchaseUnit.netWeight / 100 : 1;
  const nutriFactor = purchaseUnitFactor * (intake.amount || 0);
  return {
    fat: purchase.fat * nutriFactor,
    carbs: purchase.carbs * nutriFactor,
    protein: purchase.protein * nutriFactor,
    kcal: purchase.kcal! * nutriFactor,
  };
}

export function nutriValuesFromPersonRecipe(
  basiceRecipe: RecipeUnitModification,
  personRecipe: PersonRecipeUnitModification | undefined,
  foods: FoodDto[],
) {
  if (!personRecipe) {
    return emptyNutriValue;
  }
  const foodId = basiceRecipe.foodId;
  const food = findById(foods, foodId);
  if (!food) {
    return emptyNutriValue;
  }
  const foodUnit = food.foodUnits.find(
    (foodUnit) => foodUnit.id == basiceRecipe.foodUnitId,
  )!;
  if (!foodUnit) {
    return emptyNutriValue;
  }
  const foodUnitFactor =
    foodUnit.netWeight != undefined ? foodUnit.netWeight / 100 : 1;
  const nutriFactor = foodUnitFactor * personRecipe.amount;
  return {
    fat: food.fat * nutriFactor,
    carbs: food.carbs * nutriFactor,
    protein: food.protein * nutriFactor,
    kcal: food.kcal! * nutriFactor,
  };
}

export function nutriValuesFromFoodUnitWithAmount(
  foods: FoodDto[],
  foodUnitId: number | undefined,
  amount: number,
) {
  if (!foodUnitId) {
    return emptyNutriValue;
  }
  const food = foods.find((f) =>
    f.foodUnits.some((fu) => fu.id === foodUnitId),
  );
  if (!food) {
    return emptyNutriValue;
  }
  const foodUnit = food.foodUnits.find((fu) => fu.id == foodUnitId);
  if (!foodUnit) {
    return emptyNutriValue;
  }
  const foodUnitFactor =
    foodUnit.netWeight != undefined ? foodUnit.netWeight / 100 : 1;
  const nutriFactor = foodUnitFactor * amount;
  return {
    fat: food.fat * nutriFactor,
    carbs: food.carbs * nutriFactor,
    protein: food.protein * nutriFactor,
    kcal: food.kcal! * nutriFactor,
  };
}

export function nutriValuesFromPortion(
  basiceRecipe: RecipeUnitModification,
  portionCount: number,
  foods: FoodDto[],
) {
  if (!basiceRecipe.portionAmount) {
    return emptyNutriValue;
  }
  const foodId = basiceRecipe.foodId;
  const food = findById(foods, foodId);
  if (!food) {
    return emptyNutriValue;
  }
  const foodUnit = food.foodUnits.find(
    (foodUnit) => foodUnit.id == basiceRecipe.foodUnitId,
  )!;
  if (!foodUnit) {
    return emptyNutriValue;
  }
  const foodUnitFactor =
    foodUnit.netWeight != undefined ? foodUnit.netWeight / 100 : 1;
  const nutriFactor =
    (foodUnitFactor * basiceRecipe.portionAmount) / portionCount;
  return {
    fat: food.fat * nutriFactor,
    carbs: food.carbs * nutriFactor,
    protein: food.protein * nutriFactor,
    kcal: food.kcal! * nutriFactor,
  };
}

export function formatCoordinates(
  planCellCoordinates: PlanCellCoordinatesWithOptionalPerson,
  persons: PersonDto[],
) {
  const maybePersonSuffix = planCellCoordinates.personId
    ? ` für ${findById(persons, planCellCoordinates.personId)?.name}`
    : "";
  return `${mealInds[planCellCoordinates.mealInd]?.name || ""} am ${formatGermanDateShort(planCellCoordinates.date)}${maybePersonSuffix}`;
}

export const mapPlanCellToNutriValues = (
  planCell: PlanCell,
  foods: FoodDto[],
  purchases: PurchaseDto[],
): NutriValues[] =>
  planCell.indicator == "MealWithIntakes"
    ? planCell.intakes != undefined
      ? planCell.intakes
          .filter((i) => !isDeleted(i))
          .map((i) => nutriValuesFromIntake(i, purchases))
      : planCell.meal.mealIngredients.map((mealIngredient) =>
          nutriValuesFromMealIngredient(mealIngredient, foods, purchases),
        )
    : [planCell].map((c) => nutriValuesFromIntake(c, purchases));

export function buildIntake(
  meal: { mealDate: string; mealInd: string; personId: number; id: number },
  amount: number,
  purchase: { id: number; description: string },
  purchaseUnit: { id: number; description: string },
  sourceIngredient?: Pick<MealIngredient, "stepIndex" | "stepText">,
) {
  return {
    id: -1,
    intakeDate: meal.mealDate,
    mealInd: meal.mealInd,
    amount,
    personId: meal.personId,
    mealId: meal.id,
    purchaseUnitId: purchaseUnit.id,
    purchaseUnitDescription: purchaseUnit.description,
    purchaseUnitPurchaseId: purchase.id,
    purchaseUnitPurchaseDescription: purchase.description,
    stepIndex: sourceIngredient?.stepIndex ?? undefined,
    stepText: sourceIngredient?.stepText ?? undefined,
  };
}

export function createDefaultIntakes(
  planCell: MealWithIntakes,
  purchases: PurchaseDto[],
) {
  const selectablePurchases = purchases;
  return planCell.meal.mealIngredients.map((mealIngredient) => {
    const defaultPurchase = isNullIndex(mealIngredient.foodUnitFoodId)
      ? findById(purchases, mealIngredient.purchaseUnitPurchaseId!)!
      : selectablePurchases.find(
          (pu) => pu.foodId == mealIngredient.foodUnitFoodId && pu.isDefault,
        )!;
    const defaultPurchaseUnit = defaultPurchase.purchaseUnits.find(
      (pu) => pu.foodUnitId == mealIngredient.foodUnitId,
    )!;
    return buildIntake(
      planCell.meal,
      mealIngredient.amount,
      defaultPurchase,
      defaultPurchaseUnit,
      mealIngredient,
    );
  });
}

export const rebuildPlanCells = async (
  planCellWithIndices: PlanCellWithIndex[],
) => {
  const mealIds = planCellWithIndices.flatMap((pc) =>
    pc.planCell.indicator == "MealWithIntakes" ? pc.planCell.meal.id : [],
  );
  const mealsWithIngredients = (await updateMeals({ body: mealIds })).data;
  return mealsWithIngredients.map((mealWithIngredients) => {
    const mealWithIntakes: MealWithIntakes = {
      indicator: "MealWithIntakes",
      meal: mealWithIngredients,
    };
    return mealWithIntakes;
  });
};
export type MealKey = {
  dishId?: number;
  foodId?: number;
  purchaseId?: number;
};
export type StrictMealKey = MealKey & { personId: number };

export type MealKeyString =
  `{"dishId":${number | undefined},"foodId":${number | undefined}},"purchaseId":${number | undefined}}`;

export function getMealKeyForMeal(meal: MealWithIngredients): MealKey {
  return {
    dishId: meal.dishId,
    foodId: meal.foodId,
    purchaseId: meal.purchaseId,
  };
}

export function getStrictMealKeyForMeal(
  meal: MealWithIngredients,
): StrictMealKey {
  return {
    dishId: meal.dishId,
    foodId: meal.foodId,
    purchaseId: meal.purchaseId,
    personId: meal.personId,
  };
}

export function parseMealKey(keyString: MealKeyString): MealKey {
  return JSON.parse(keyString);
}

export function getMealKeyAsString(meal: MealWithIntakes): MealKeyString {
  const { dishId, foodId, purchaseId } = meal.meal;
  return JSON.stringify({
    dishId,
    foodId,
    purchaseId,
  }) as MealKeyString;
}

export const nutriValueSumReducer = (a: NutriValues, b: NutriValues) => {
  return {
    fat: a.fat + b.fat,
    carbs: a.carbs + b.carbs,
    protein: a.protein + b.protein,
    kcal: a.kcal + b.kcal,
  };
};
export type HistoryState = { id: string };

export type IngredientPair = { mi: MealIngredient; meal: MealWithIntakes };

export type IngredientGroup = {
  key: string;
  pairs: IngredientPair[];
  name: string;
  isFoodBased: boolean;
  recipeIngredient?: StepIngredientModification;
};

export interface IngredientOverride {
  personAmounts: Record<number, number>; // personId -> amount
  unitId: number | undefined;
  purchaseId: number | null | undefined; // null = Planwert, undefined = not captured yet
  deleted?: boolean;
}

export type CookingOverrides = Record<string, IngredientOverride>;

/**
 * Eindeutiger Schlüssel einer MealIngredient für Overrides (z. B. im Kochdialog).
 * Der `stepIndex` ist Teil des Schlüssels, damit dieselbe Food in
 * unterschiedlichen Schritten nicht denselben Override teilt (sonst überschreibt
 * das zuletzt gewählte Produkt die Auswahl der anderen Schritte).
 */
export function getMIKey(mi: MealIngredient): string {
  const base = isNullIndex(mi.foodUnitFoodId)
    ? `purchase-${mi.purchaseUnitPurchaseId}-${mi.purchaseUnitId}`
    : `food-${mi.foodUnitFoodId}-${mi.foodUnitId}`;
  return mi.stepIndex != null ? `${base}-step-${mi.stepIndex}` : base;
}

/**
 * Die aktuelle Einheit einer Zutatengruppe – entweder eine `FoodUnit` oder eine
 * `PurchaseUnit`. Der `kind`-Diskriminator macht überall explizit, welcher Typ
 * vorliegt (statt eine PurchaseUnit als FoodUnit auszugeben).
 */
export type GroupUnits =
  | {
      kind: "food";
      unitId: number | undefined;
      current: FoodUnit | undefined;
      selectable: FoodUnit[];
    }
  | {
      kind: "purchase";
      unitId: number | undefined;
      current: PurchaseUnit | undefined;
      selectable: PurchaseUnit[];
    };

export interface GroupOverrideState {
  personAmounts: Record<number, number>;
  totalAmount: number;
  purchaseId: number | null | undefined;
  deleted: boolean;
  units: GroupUnits;
}

export function resolveFoodUnitId(
  unitId: number | undefined,
  purchaseId: number | null | undefined,
  purchases: PurchaseDto[],
  foods: FoodDto[],
): number | undefined {
  if (!unitId) return undefined;

  // If a purchase is active, unitId is a PurchaseUnit ID!
  if (purchaseId !== null && purchaseId !== undefined) {
    const purchase = findById(purchases, purchaseId);
    if (purchase) {
      const pu = purchase.purchaseUnits?.find((u: any) => u.id === unitId);
      if (pu) {
        if (pu.foodUnitId) return pu.foodUnitId;
        const food = findById(foods, purchase.foodId);
        if (food && food.foodUnits?.length > 0) {
          const matchingFU = food.foodUnits.find(
            (fu) => fu.netWeight === pu.netWeight,
          );
          if (matchingFU) return matchingFU.id;
          const gramFU = food.foodUnits.find(
            (fu) =>
              fu.netWeight === 1 ||
              fu.description.toLowerCase() === "g" ||
              fu.description.toLowerCase() === "1g",
          );
          if (gramFU) return gramFU.id;
          return food.foodUnits[0].id;
        }
      }
    }
    // Fallback: search all purchases if purchaseId lookup fails
    for (const p of purchases) {
      const pu = p.purchaseUnits?.find((u: any) => u.id === unitId);
      if (pu) {
        if (pu.foodUnitId) return pu.foodUnitId;
        const food = findById(foods, p.foodId);
        if (food && food.foodUnits?.length > 0) {
          const matchingFU = food.foodUnits.find(
            (fu) => fu.netWeight === pu.netWeight,
          );
          if (matchingFU) return matchingFU.id;
          const gramFU = food.foodUnits.find(
            (fu) =>
              fu.netWeight === 1 ||
              fu.description.toLowerCase() === "g" ||
              fu.description.toLowerCase() === "1g",
          );
          if (gramFU) return gramFU.id;
          return food.foodUnits[0].id;
        }
      }
    }
  }

  // Otherwise, it's already a FoodUnit ID!
  return unitId;
}

export function resolveGroupOverrideState(
  group: IngredientGroup,
  override: IngredientOverride | undefined,
  purchases: PurchaseDto[],
  foods: FoodDto[],
): GroupOverrideState {
  const firstIngredient = group.pairs[0].mi;

  const currentUnitId =
    override?.unitId !== undefined
      ? override.unitId
      : isNullIndex(firstIngredient.foodUnitId)
        ? undefined
        : firstIngredient.foodUnitId;

  const currentPurchaseId = override?.purchaseId;
  const foodUnitId = resolveFoodUnitId(
    currentUnitId,
    currentPurchaseId,
    purchases,
    foods,
  );

  const food = foodUnitId
    ? foods.find((f) => f.foodUnits.some((fu) => fu.id === foodUnitId))
    : undefined;

  const currentPersonAmounts = override
    ? override.personAmounts
    : (() => {
        const defaultAmounts: Record<number, number> = {};
        group.pairs.forEach((p) => {
          defaultAmounts[p.meal.meal.personId] = p.mi.amount;
        });
        return defaultAmounts;
      })();

  const currentTotalAmount = Object.values(currentPersonAmounts).reduce(
    (sum, a) => sum + a,
    0,
  );

  let units: GroupUnits;
  if (currentPurchaseId) {
    const purchase = findById(purchases, currentPurchaseId);
    const selectable: PurchaseUnit[] = purchase?.purchaseUnits ?? [];
    units = {
      kind: "purchase",
      unitId: currentUnitId,
      current: selectable.find((pu) => pu.id === currentUnitId),
      selectable,
    };
  } else {
    const selectable: FoodUnit[] = (food?.foodUnits ?? []) as FoodUnit[];
    units = {
      kind: "food",
      unitId: currentUnitId,
      current: selectable.find((fu) => fu.id === currentUnitId),
      selectable,
    };
  }

  return {
    personAmounts: currentPersonAmounts,
    totalAmount: currentTotalAmount,
    purchaseId: currentPurchaseId,
    deleted: override?.deleted ?? false,
    units,
  };
}

export function createCustomIntakes(
  planCell: MealWithIntakes,
  purchases: PurchaseDto[],
  foods: FoodDto[],
  overrides: CookingOverrides,
): { intakes: any[]; updatedMeal?: MealWithIngredients } {
  const selectablePurchases = purchases;
  const intakes: any[] = [];
  let isMealEdited = false;
  let updatedMealIngredients = [...planCell.meal.mealIngredients];
  let updatedPurchase: Purchase | undefined = undefined;

  for (const mealIngredient of planCell.meal.mealIngredients) {
    const key = getMIKey(mealIngredient);
    const override = overrides[key];

    const isPurchaseBased = isNullIndex(mealIngredient.foodUnitFoodId);

    const group: IngredientGroup = {
      key,
      pairs: [{ mi: mealIngredient, meal: planCell }],
      name:
        mealIngredient.foodUnitFoodDescription ||
        mealIngredient.purchaseUnitPurchaseDescription ||
        "",
      isFoodBased: !isPurchaseBased,
    };

    const groupState = resolveGroupOverrideState(
      group,
      override,
      purchases,
      foods,
    );

    if (groupState.deleted) {
      continue;
    }

    const personAmount = groupState.personAmounts[planCell.meal.personId];
    if (personAmount === undefined || personAmount <= 0) {
      continue;
    }

    if (groupState.purchaseId !== null && groupState.purchaseId !== undefined) {
      const purchase = findById(purchases, groupState.purchaseId);
      if (purchase) {
        const pu =
          purchase.purchaseUnits?.find(
            (pu) => pu.id === groupState.units.unitId,
          ) || purchase.purchaseUnits?.[0];
        if (pu) {
          intakes.push(
            buildIntake(planCell.meal, personAmount, purchase, pu, mealIngredient),
          );

          // If purchase-based and a different purchase was selected, update the planned meal
          if (
            isPurchaseBased &&
            purchase.id !== mealIngredient.purchaseUnitPurchaseId
          ) {
            isMealEdited = true;
            updatedMealIngredients = updatedMealIngredients.map((mi) => {
              if (mi.id === mealIngredient.id) {
                return {
                  ...mi,
                  purchaseUnitPurchaseId: purchase.id,
                  purchaseUnitPurchaseDescription: purchase.description,
                  purchaseUnitId: pu.id,
                  purchaseUnitDescription: pu.description,
                  amount: personAmount,
                };
              }
              return mi;
            });
            updatedPurchase = purchase;
          }
          continue;
        }
      }
    }

    const resolvedFoodUnitId = resolveFoodUnitId(
      groupState.units.unitId,
      groupState.purchaseId,
      purchases,
      foods,
    );
    const activeFood = resolvedFoodUnitId
      ? foods.find((f) =>
          f.foodUnits.some((fu) => fu.id === resolvedFoodUnitId),
        )
      : undefined;
    const defaultPurchase = isPurchaseBased
      ? findById(purchases, mealIngredient.purchaseUnitPurchaseId)
      : activeFood
        ? findById(purchases, activeFood.defaultPurchaseId)
        : undefined;

    if (defaultPurchase) {
      const pu =
        defaultPurchase.purchaseUnits?.find(
          (pu) =>
            (isPurchaseBased && pu.id === mealIngredient.purchaseUnitId) ||
            (pu.foodUnitId === resolvedFoodUnitId &&
              resolvedFoodUnitId !== undefined),
        ) || defaultPurchase.purchaseUnits?.[0];
      if (pu) {
        intakes.push(
          buildIntake(
            planCell.meal,
            personAmount,
            defaultPurchase,
            pu,
            mealIngredient,
          ),
        );
        continue;
      }
    }

    // Fallback bezieht sich auf das *aufgelöste* Food (Alternative) – nicht das
    // ursprüngliche Food der MealIngredient.
    const fallbackFoodId = isPurchaseBased
      ? undefined
      : (activeFood?.id ?? mealIngredient.foodUnitFoodId);
    const defaultPurchaseFallback = isPurchaseBased
      ? findById(purchases, mealIngredient.purchaseUnitPurchaseId!)
      : fallbackFoodId != null && !isNullIndex(fallbackFoodId)
        ? selectablePurchases.find(
            (pu) => pu.foodId === fallbackFoodId && pu.isDefault,
          )
        : undefined;
    if (defaultPurchaseFallback) {
      const defaultPurchaseUnit = defaultPurchaseFallback.purchaseUnits?.find(
        (pu) =>
          pu.foodUnitId === resolvedFoodUnitId ||
          pu.foodUnitId === mealIngredient.foodUnitId,
      );
      if (defaultPurchaseUnit) {
        intakes.push(
          buildIntake(
            planCell.meal,
            personAmount,
            defaultPurchaseFallback,
            defaultPurchaseUnit,
            mealIngredient,
          ),
        );
      }
    }
  }

  return {
    intakes,
    updatedMeal: isMealEdited
      ? {
          ...planCell.meal,
          purchaseId: updatedPurchase!.id,
          purchaseDescription: formatPurchaseDescription(updatedPurchase),
          mealIngredients: updatedMealIngredients,
        }
      : undefined,
  };
}
export function canBeConsumed(planCell: PlanCellWithIndex | undefined) {
  return !(
    planCell?.planCell.indicator == "MealWithIntakes" &&
    isNew(planCell?.planCell.meal)
  );
}
